import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { post as apiPost } from '../services/apiService';
import { getActiveTeam } from '../services/profileService';
import { logError } from '../services/loggerService';
import { showInvitationsSentSuccess, showInvitationsPartialSuccess, showInvitationsFailed } from '../services/toastService';

export type InviteStatus = 'pending' | 'success' | 'error';

export interface InviteResult {
  email: string;
  displayName: string;
  status: InviteStatus;
  errorMessage?: string;
}

interface InvitationContextType {
  inviteResults: Map<string, InviteResult>;
  isInviting: boolean;
  sendInvitations: (invites: Array<{ email: string; displayName: string }>, message?: string) => Promise<void>;
  clearInviteResults: () => void;
}

const InvitationContext = createContext<InvitationContextType | undefined>(undefined);

export function InvitationProvider({ children }: { children: ReactNode }) {
  const [inviteResults, setInviteResults] = useState<Map<string, InviteResult>>(new Map());
  const [isInviting, setIsInviting] = useState(false);

  const sendInvitations = async (
    invites: Array<{ email: string; displayName: string }>,
    message?: string
  ): Promise<void> => {
    const team = getActiveTeam();
    if (!team?.id) {
      throw new Error('No active team available');
    }

    setIsInviting(true);
    const newResults = new Map<string, InviteResult>();

    // Initialize all invites as pending (use trimmed email as key)
    invites.forEach(({ email, displayName }) => {
      const trimmedEmail = email.trim();
      newResults.set(trimmedEmail, {
        email: trimmedEmail,
        displayName: displayName.trim(),
        status: 'pending',
      });
    });
    setInviteResults(new Map(newResults));

    // Send invitations for each email
    const invitePromises = invites.map(async ({ email, displayName }) => {
      const trimmedEmail = email.trim();
      const trimmedDisplayName = displayName.trim();
      
      try {
        await apiPost<void>('/add-profile-to-business', {
          email: trimmedEmail,
          businessId: team.id,
          displayName: trimmedDisplayName,
          message: message?.trim(),
          persona: 'business',
          role: 'user',
          delayNotification: false,
        });

        newResults.set(trimmedEmail, {
          email: trimmedEmail,
          displayName: trimmedDisplayName,
          status: 'success',
        });

        setInviteResults(prev => {
          const updated = new Map(prev);
          updated.set(trimmedEmail, {
            email: trimmedEmail,
            displayName: trimmedDisplayName,
            status: 'success',
          });
          return updated;
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to send invitation';
        newResults.set(trimmedEmail, {
          email: trimmedEmail,
          displayName: trimmedDisplayName,
          status: 'error',
          errorMessage,
        });

        setInviteResults(prev => {
          const updated = new Map(prev);
          updated.set(trimmedEmail, {
            email: trimmedEmail,
            displayName: trimmedDisplayName,
            status: 'error',
            errorMessage,
          });
          return updated;
        });
        logError(error as Error, `Failed to invite ${trimmedEmail}`);
      }
    });

    await Promise.all(invitePromises);
    setIsInviting(false);

    // Show toast notification based on results
    const resultsArray = Array.from(newResults.values());
    const successCount = resultsArray.filter(r => r.status === 'success').length;
    const errorCount = resultsArray.filter(r => r.status === 'error').length;

    if (successCount > 0 && errorCount === 0) {
      // All successful
      await showInvitationsSentSuccess(successCount);
    } else if (successCount > 0 && errorCount > 0) {
      // Some succeeded, some failed
      await showInvitationsPartialSuccess(successCount, errorCount);
    } else if (errorCount > 0) {
      // All failed
      await showInvitationsFailed(errorCount);
    }
  };

  const clearInviteResults = useCallback(() => {
    setInviteResults(new Map());
  }, []);

  return (
    <InvitationContext.Provider
      value={{
        inviteResults,
        isInviting,
        sendInvitations,
        clearInviteResults,
      }}
    >
      {children}
    </InvitationContext.Provider>
  );
}

export function useInvitation() {
  const context = useContext(InvitationContext);
  if (context === undefined) {
    throw new Error('useInvitation must be used within an InvitationProvider');
  }
  return context;
}

